import { useState } from 'react';
import type { ManualLeadStatus } from '@auctionradar/shared';
import { useUpdateLeadStatus } from '../api/hooks';

const ACTIONS: Array<{
  status: ManualLeadStatus;
  label: string;
  /** Tailwind class triplet: idle / hover / active. */
  tone: string;
}> = [
  {
    status: 'watching',
    label: '👀 Παρακολούθηση',
    tone: 'border-sky-300 bg-white text-sky-700 hover:bg-sky-50',
  },
  {
    status: 'bidding',
    label: '🚀 Προσφορά',
    tone: 'border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50',
  },
  {
    status: 'reviewed',
    label: '✅ Έλεγχος ΟΚ',
    tone: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
  },
  {
    status: 'dismissed',
    label: '🗑️ Απόρριψη',
    tone: 'border-rose-300 bg-white text-rose-700 hover:bg-rose-50',
  },
];

export function StatusActions({
  leadId,
  currentStatus,
}: {
  leadId: string;
  currentStatus: string;
}) {
  const mutation = useUpdateLeadStatus(leadId);
  const [showNote, setShowNote] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ManualLeadStatus | null>(
    null,
  );
  const [note, setNote] = useState('');

  const submit = (status: ManualLeadStatus, withNote: boolean) => {
    mutation.mutate(
      {
        status,
        ...(withNote && note.trim().length > 0 && { note: note.trim() }),
      },
      {
        onSuccess: () => {
          setShowNote(false);
          setPendingStatus(null);
          setNote('');
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ενέργειες
        </h3>
        <span className="text-xs text-slate-500">
          Τρέχον status: <span className="font-medium text-slate-900">{currentStatus}</span>
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {ACTIONS.map((a) => {
          const isCurrent = a.status === currentStatus;
          return (
            <button
              key={a.status}
              disabled={isCurrent || mutation.isPending}
              onClick={() => {
                if (a.status === 'dismissed') {
                  // Dismissal usually warrants a reason — open the note input.
                  setPendingStatus(a.status);
                  setShowNote(true);
                } else {
                  submit(a.status, false);
                }
              }}
              className={`rounded border px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${a.tone}`}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      {showNote && pendingStatus && (
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
          <label className="mb-1 block text-xs font-medium text-slate-700">
            Λόγος απόρριψης (προαιρετικό):
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded border border-slate-300 bg-white p-2 text-sm"
            placeholder="π.χ. Πιθανό συγκυριακό. ΑΦΜ φαίνεται ίδιο με #X-001..."
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => {
                setShowNote(false);
                setPendingStatus(null);
                setNote('');
              }}
              className="rounded px-3 py-1 text-sm text-slate-600 hover:bg-slate-100"
              disabled={mutation.isPending}
            >
              Άκυρο
            </button>
            <button
              onClick={() => submit(pendingStatus, true)}
              className="rounded bg-rose-600 px-3 py-1 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Αποθήκευση...' : 'Επιβεβαίωση'}
            </button>
          </div>
        </div>
      )}

      {mutation.isError && (
        <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          Αποτυχία: {(mutation.error as Error).message}
        </div>
      )}
    </div>
  );
}
